import React, { useMemo, useState, useEffect } from 'react';
import {
  useGetMyNotificationsQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
  useClearAllNotificationsMutation,
  useDeleteNotificationMutation,
} from '../../services/notificationApi';
import { BellIcon, TrashIcon, CheckIcon, EyeIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { formatDateTimeDDMMYYYY } from '../../utils/dateUtils';
import toast from 'react-hot-toast';

const TypePill = ({ type }) => {
  const map = {
    success: 'bg-emerald-500 text-white ring-emerald-200',
    warning: 'bg-amber-500 text-white ring-amber-200',
    error: 'bg-red-500 text-white ring-red-200',
    info: 'bg-blue-500 text-white ring-blue-200'
  };
  const label = type?.[0]?.toUpperCase() + type?.slice(1);
  return (
    <span className={`inline-flex items-center px-4 py-1.5 rounded-lg text-sm font-semibold shadow-sm ring-2 ${map[type] || map.info}`}>
      {label || 'Info'}
    </span>
  );
};

const IconFor = ({ type }) => {
  const bgMap = {
    success: 'bg-emerald-500',
    warning: 'bg-amber-500',
    error: 'bg-red-500',
    info: 'bg-blue-500'
  };

  const iconMap = {
    success: <CheckCircleIcon className="h-5 w-5 text-white" />,
    warning: <ExclamationTriangleIcon className="h-5 w-5 text-white" />,
    error: <XMarkIcon className="h-5 w-5 text-white" />,
    info: <InformationCircleIcon className="h-5 w-5 text-white" />
  };

  return (
    <div className={`p-2 rounded-full shadow-md ${bgMap[type] || bgMap.info}`}>
      {iconMap[type] || iconMap.info}
    </div>
  );
};

export default function NotificationsPage() {
  const [page, setPage] = useState(1);
  const limit = 20;
  const { data, isFetching, refetch, isLoading } = useGetMyNotificationsQuery({ page, limit });
  const [markRead] = useMarkNotificationReadMutation();
  const [markAllRead] = useMarkAllNotificationsReadMutation();
  const [clearAll] = useClearAllNotificationsMutation();
  const [deleteOne] = useDeleteNotificationMutation();

  const notifications = useMemo(() => (data?.data?.notifications || []), [data]);
  const total = data?.data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  console.log('📋 NotificationsPage render:', { notifications: notifications.length, total, isFetching, isLoading });

  useEffect(() => {
    console.log('📋 Notifications data changed:', { notifications: notifications.length, total });
  }, [notifications, total]);

  const onDelete = async (id) => {
    try {
      await deleteOne(id).unwrap();
      toast.success('Notification deleted successfully');
      refetch();
    } catch (error) {
      console.error('Error deleting notification:', error);
      const errorMessage = error?.data?.message || error?.message || 'Failed to delete notification';
      toast.error(errorMessage);
    }
  };

  const onMark = async (id) => {
    try {
      await markRead(id).unwrap();
      toast.success('Notification marked as read');
      refetch();
    } catch (error) {
      console.error('Error marking notification as read:', error);
      const errorMessage = error?.data?.message || error?.message || 'Failed to mark notification as read';
      toast.error(errorMessage);
    }
  };

  const onMarkAll = async () => {
    try {
      await markAllRead().unwrap();
      toast.success('All notifications marked as read');
      refetch();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      const errorMessage = error?.data?.message || error?.message || 'Failed to mark all notifications as read';
      toast.error(errorMessage);
    }
  };

  const onClearAll = async () => {
    const confirmed = window.confirm('Are you sure you want to clear all notifications? This action cannot be undone.');
    if (!confirmed) return;

    try {
      await clearAll().unwrap();
      toast.success('All notifications cleared successfully');
      setPage(1);
      refetch();
    } catch (error) {
      console.error('Error clearing notifications:', error);
      const errorMessage = error?.data?.message || error?.message || 'Failed to clear notifications';
      toast.error(errorMessage);
    }
  };

  const typeBorderMap = {
    success: 'border-emerald-400',
    warning: 'border-amber-400',
    error: 'border-red-400',
    info: 'border-blue-400'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 px-4 py-8 lg:px-8 animate-fade-in">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 animate-slide-down">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div className="flex items-center space-x-4">
              <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-3 rounded-2xl shadow-lg animate-bounce-in">
                <BellIcon className="h-8 w-8 text-white animate-pulse-once" />
              </div>
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 animate-slide-right">
                  Your Notifications
                </h1>
                <p className="text-gray-600 mt-1 animate-fade-in-delayed">Keep track of important updates and activities</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={onMarkAll}
                disabled={notifications.length === 0}
                className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105 disabled:transform-none disabled:cursor-not-allowed flex items-center gap-2 text-sm animate-fade-in-delayed"
              >
                <CheckIcon className="h-4 w-4" />
                Mark All Read
              </button>
              <button
                onClick={onClearAll}
                disabled={notifications.length === 0}
                className="px-4 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105 disabled:transform-none disabled:cursor-not-allowed flex items-center gap-2 text-sm animate-fade-in-delayed"
              >
                <TrashIcon className="h-4 w-4" />
                Clear All
              </button>
            </div>
          </div>
        </div>

        {/* Notifications List */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden animate-slide-up">
          {isFetching ? (
            <div className="p-8 text-center animate-fade-in">
              <div className="flex justify-center items-center space-x-3">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-200 border-t-blue-600"></div>
                <p className="text-gray-600 font-medium">Loading your notifications...</p>
              </div>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-16 text-center">
              <div className="bg-gray-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <BellIcon className="h-8 w-8 text-gray-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Notifications</h3>
              <p className="text-gray-600">You're all caught up! New notifications will appear here.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {notifications.map((n, index) => (
                <div
                  key={n._id}
                  className={`p-6 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50/50 transition-all duration-300 transform hover:scale-[1.02] animate-slide-in ${
                    !n.read ? 'bg-blue-50/30 border-l-4 border-blue-500 shadow-sm' : 'border-l-4 bg-white'
                  } ${typeBorderMap[n.type] || 'border-blue-400'}`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <IconFor type={n.type} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">{n.title}</h3>
                            {!n.read && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Unread
                              </span>
                            )}
                          </div>
                          <p className="text-gray-700 mb-3 leading-relaxed">{n.message}</p>
                          <div className="flex items-center gap-3">
                            <TypePill type={n.type} />
                            <span className="text-sm text-gray-500 flex items-center gap-1">
                              <EyeIcon className="h-4 w-4" />
                              {formatDateTimeDDMMYYYY(n.createdAt)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-auto">
                        {!n.read && (
                          <button
                            onClick={() => onMark(n._id)}
                            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-all duration-200 transform hover:scale-105 flex items-center gap-1 shadow-md hover:shadow-lg"
                          >
                            <CheckIcon className="h-3.5 w-3.5" />
                            Mark Read
                          </button>
                        )}
                        <button
                          onClick={() => onDelete(n._id)}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200 transform hover:scale-110 animate-pulse-on-hover"
                          title="Delete notification"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-xl shadow-lg p-6 border border-gray-200 animate-slide-up-delayed">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-6 py-3 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-300 text-white rounded-lg font-medium shadow-sm transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 transform hover:scale-105 text-sm w-full sm:w-auto"
            >
              Previous
            </button>
            <div className="bg-gray-100 px-4 py-2 rounded-lg text-gray-700 font-medium text-sm shadow-sm">
              Page {page} of {totalPages}
            </div>
            <button
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg font-medium shadow-sm transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 transform hover:scale-105 text-sm w-full sm:w-auto"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
